// Parse the structured JSON output of `grade_session` into a typed object
// the Recap page can render. Tolerant of:
//   - leading "[Job xxx] Starting…" / orchestration noise
//   - ```json …``` or ``` …``` wrappers some models add despite format=json
//   - trailing "✓ Saved to interview-prep/…" lines
//   - leading/trailing whitespace
//
// Returns null if no parseable JSON object can be found.

import { cleanLLMOutput } from './clean-llm-output';

export interface GradeAxis {
  name: string;
  score: number;        // 0-10
  feedback: string;
  quote?: string;
}

export interface GradeQuestionSummary {
  question: string;
  summary: string;
  rating: 'strong' | 'adequate' | 'weak' | string;
}

export interface GradeSessionResult {
  overallScore: number; // 0-100
  overallFeedback: string;
  axes: GradeAxis[];
  questionSummaries: GradeQuestionSummary[];
  softSkillSummary: string;
}

// Find the largest balanced { … } substring in a noisy log. We can't just
// regex-greedy-match because LLM output sometimes contains stray braces in
// quoted feedback. Walk char-by-char counting depth.
function extractFirstBalancedObject(s: string): string | null {
  const start = s.indexOf('{');
  if (start < 0) return null;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < s.length; i++) {
    const c = s[i];
    if (escape) { escape = false; continue; }
    if (inString) {
      if (c === '\\') { escape = true; continue; }
      if (c === '"') inString = false;
      continue;
    }
    if (c === '"') { inString = true; continue; }
    if (c === '{') depth++;
    else if (c === '}') {
      depth--;
      if (depth === 0) return s.slice(start, i + 1);
    }
  }
  return null;
}

export function parseGradeJson(raw: string): GradeSessionResult | null {
  if (!raw) return null;
  let s = cleanLLMOutput(raw);
  // Strip ```json … ``` wrapper if model added it despite format=json
  s = s.replace(/```(?:json)?\s*/gi, '').replace(/```/g, '');
  const obj = extractFirstBalancedObject(s);
  if (!obj) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(obj);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const p = parsed as Record<string, unknown>;
  const axes = Array.isArray(p.axes)
    ? (p.axes as Record<string, unknown>[]).map((a) => ({
        name: String(a.name ?? ''),
        score: Number(a.score ?? 0),
        feedback: String(a.feedback ?? ''),
        quote: a.quote != null ? String(a.quote) : undefined,
      }))
    : [];
  const qs = Array.isArray(p.questionSummaries)
    ? (p.questionSummaries as Record<string, unknown>[]).map((q) => ({
        question: String(q.question ?? ''),
        summary: String(q.summary ?? ''),
        rating: String(q.rating ?? 'adequate'),
      }))
    : [];
  return {
    overallScore: Number(p.overallScore ?? 0),
    overallFeedback: String(p.overallFeedback ?? ''),
    axes,
    questionSummaries: qs,
    softSkillSummary: String(p.softSkillSummary ?? ''),
  };
}

export interface ProbeResult {
  move_on: boolean;
  probe?: string;
  reason?: string;
}

export function parseProbeJson(raw: string): ProbeResult | null {
  if (!raw) return null;
  let s = cleanLLMOutput(raw);
  s = s.replace(/```(?:json)?\s*/gi, '').replace(/```/g, '');
  const obj = extractFirstBalancedObject(s);
  if (!obj) return null;
  try {
    const parsed = JSON.parse(obj) as Record<string, unknown>;
    return {
      move_on: parsed.move_on === true,
      probe: parsed.probe != null ? String(parsed.probe) : undefined,
      reason: parsed.reason != null ? String(parsed.reason) : undefined,
    };
  } catch {
    return null;
  }
}
